// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import Promise from 'bluebird';
import $ from 'jquery';
import L from 'leaflet';
import myw from 'myWorld/base/core';

/* global google */

/**
 * Module that loads the Google Maps API library. <br/>
 *  Modules that will use the Google Maps API library should require this module and then listen
 *  for the application's  {@link event:googleMapsApi-loaded} <br/>
 *  @example myw.googleMapsLibrary.load();
 *  @example myw.googleMapsLibrary.load().then(() => {
 *      //code depending on google maps library
 *  });
 *  @module
 */

myw.googleMapsLibrary = {
    loaded: false,
    nChecks: 0,
    promise: null,

    urlParameters: {
        v: 'quarterly', //see https://developers.google.com/maps/documentation/javascript/versions
        sensor: 'true',
        libraries: 'places,weather',
        callback: 'myw.gmapsapicallback'
    },

    load(options) {
        if (!this.promise) {
            const self = this;
            this.promise = new Promise((resolve, reject) => {
                //setup global callback for when google maps api has finished loading,
                //in a way that it resolves the promise
                myw.gmapsapicallback = self._callback.bind(self, resolve);

                //do the actual loading of the library
                self._load(options);

                //for the situation where the library failed to load (ex: no internet access)
                //we'll setup a handler to retry when internet becomes available
                myw.appReady &&
                    myw.appReady.then(app => {
                        //app does an initial check of internet status which throws an event when it is changed from undefined
                        //we want to ignore this first event, as an initial load as already been started - if the load fails we
                        //want to retry on the next internetStatus-changed event that changes the status to true
                        //ENH: ugly - each load operation should be another promise that rejects on a timeout?
                        let initial = true;
                        app.on('internetStatus-changed', e => {
                            if (!self.loaded && e.hasInternetAccess && !initial) self._load();
                            initial = false;
                        });
                    });
            });
        }

        return this.promise;
    },

    _load(options) {
        if (typeof google == 'undefined' || !google.maps.Map) {
            myw.trace('googleMapsLibrary', 2, 'Loading Google Maps library');
            let paramsStr, url;

            //convert into an url parameters string
            paramsStr = _.chain(this.urlParameters)
                .extend(options)
                .map((value, key) => {
                    if (value) return key + '=' + value;
                })
                .compact()
                .value()
                .join('&');
            url = 'https://maps.googleapis.com/maps/api/js?' + paramsStr;

            $.getScript(url);

            //IE the callback isn't always called, so we'll call it with a timeout to check "manually"
            if (L.Browser.ie) setTimeout(myw.gmapsapicallback, 150);
        } else {
            //we're retrying but it has finished loading after all
            myw.gmapsapicallback();
        }
    },

    _callback(resolve) {
        if (this.loaded) return;

        if (typeof google != 'undefined' && google.maps.Map) {
            myw.trace('googleMapsLibrary', 2, 'Google Maps library successfully loaded');
            this.loaded = true;
            resolve();
            myw.appReady &&
                myw.appReady.then(app => {
                    //create a dummy map, which will force the loading of missing parts of the library
                    //this prevents future map instantiations from failing silently if internet is not available at that point)
                    //(do this after "appReady" since applying the layout replaces the body content and that could cause an error in google's code)
                    const container = L.DomUtil.create(
                        'div',
                        'leaflet-top leaflet-left',
                        document.body
                    );
                    new google.maps.Map(container);
                    $(container).remove();

                    app.fire('googleMapsApi-loaded');
                });
        } else {
            //timeout expired and library not loaded
            if (typeof google != 'undefined') {
                //google global exists but Maps library does not (can happen in IE, which seems to not always run the scripts it loads)
                //retry loading library (after a short while to give it a second change of completing the load)
                setTimeout(this._load.bind(this), 250);
            } else if (this.nChecks < 20) {
                // inside 20*0.250=5 seconds, keep trying
                this.nChecks++;
                setTimeout(myw.gmapsapicallback, 250);
            } else {
                //give up. wait for internetStatus-changed
                myw.trace(
                    'googleMapsLibrary',
                    3,
                    "Given up checking. Waiting for 'internetStatus-changed' event"
                );
            }
        }
    }
};

export default myw.googleMapsLibrary;
